import * as Sentry from '@sentry/angular';
import { environment } from '@up/environment';
import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { AeTokenInterceptor } from './app/interceptor/token.interceptor';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { IsProspectNotLogged } from './app/guards/is-prospect-not-logged.guard';
import { IsProspectLogged } from './app/guards/is-prospect-logged.guard';
import { RouterModule } from '@angular/router';
import { ErrorHandler, importProvidersFrom } from '@angular/core';

function getCurrentEnv(): 'integration' | 'qualif' | 'production' | 'development' | 'test' | 'tode' {
  if (environment.integration) {
    return 'integration';
  } else if (environment.qualif) {
    return 'qualif';
  } else if (environment.test) {
    return 'test';
  } else if (environment.production) {
    return 'production';
  } else if (environment.tode) {
    return 'tode';
  } else {
    return 'development';
  }
}

function sentryShouldCreateSpanForOutgoingRequest(url: string): boolean {
  try {
    const pathname = new URL(url, globalThis.location?.origin ?? 'http://localhost').pathname;
    if (pathname === '/' || pathname === '') {
      return false;
    }
    if (pathname === '/api' || pathname.startsWith('/api/')) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function sentryIsNoisyBrowserTransactionName(name: string | undefined): boolean {
  const n = (name ?? '').trim();
  return n === '' || n === '(no value)' || n === '/';
}

Sentry.init({
  dsn: 'https://a53193ea49ebdb789665dab2ab591dd7@o1171300.ingest.sentry.io/4506400106872832',
  environment: getCurrentEnv(),
  enabled: getCurrentEnv() !== 'development',
  tracePropagationTargets: ['localhost', 'http://localhost', 'http://localhost:3000'],
  integrations: [
    Sentry.replayIntegration({
      maskAllInputs: true,
      maskAllText: true
    }),
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest: sentryShouldCreateSpanForOutgoingRequest
    })
  ],
  ignoreErrors: [
    'Non-Error exception captured',
    'ResizeObserver loop limit exceeded',
    /^Loading chunk [\w-]+ failed/,
    'Failed to fetch dynamically imported module'
  ],
  beforeSend(event) {
    const msg = event.exception?.values?.[0]?.value ?? event.message ?? '';
    if (typeof msg === 'string' && (msg.includes('AbortError') || msg.includes('The user aborted'))) {
      return null;
    }
    if (event.exception?.values?.[0]?.type === 'ChunkLoadError') {
      return null;
    }
    return event;
  },
  tracesSampler: context => {
    if (context?.location?.hash === '#/auth/login') {
      return 0;
    }
    if (sentryIsNoisyBrowserTransactionName(context.name)) {
      return 0;
    }
    return 0.1;
  },
  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: 0.01,
  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  replaysOnErrorSampleRate: 1.0
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(CommonModule, BrowserModule, RouterModule, AppRoutingModule),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true
        // showDialog: true
      })
    },
    IsProspectLogged,
    IsProspectNotLogged,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AeTokenInterceptor,
      multi: true
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch())
  ]
}).catch(err => console.error(err));
